import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { FadeUp, SectionLabel } from "../components/Motion";
import { buildWhatsappUrl, buildEnquiryMessage, PHONE_DISPLAY, PHONE_TEL, EMAIL, ADDRESS } from "../lib/whatsapp";
import { createEnquiry } from "../services/enquiriesApi";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", requirement: "", message: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const waUrl = buildWhatsappUrl("Hello, I'd like to know more about Sanggavai Constructions.");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.phone.trim()) errs.phone = "Please enter your phone";
    else if (!/^[0-9+\-\s()]{7,}$/.test(form.phone.trim())) errs.phone = "Enter a valid phone number";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const enquiry = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      requirement: form.requirement.trim(),
      message: form.message.trim(),
      project: form.requirement.trim(),
      source: "contact_page",
    };

    setIsSubmitting(true);

    try {
      await createEnquiry(enquiry);

      const msg = buildEnquiryMessage(enquiry);
      toast.success("Enquiry saved. Redirecting to WhatsApp...");
      setForm({ name: "", phone: "", requirement: "", message: "" });
      setTimeout(() => window.open(buildWhatsappUrl(msg), "_blank", "noopener"), 600);
    } catch (error) {
      console.error("Contact enquiry submission error:", error);
      toast.error(error.message || "The enquiry could not be saved. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#FAFAFA]">
      <section className="pt-36 md:pt-44 pb-16 md:pb-20">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl text-center">
          <FadeUp>
            <SectionLabel>Contact</SectionLabel>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-6 tracking-tight text-[#1A1A1A]">
              Let's <span className="italic brand-gradient-text">talk.</span>
            </h1>
            <p className="mt-8 text-[#555] text-lg md:text-xl leading-relaxed max-w-2xl mx-auto">
              Share your requirement — we'll get back within one business day.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Contact info */}
          <FadeUp className="lg:col-span-5 space-y-4">
            <div className="bg-white rounded-2xl border border-black/5 p-8">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-full brand-gradient flex items-center justify-center text-white shrink-0"><MapPin className="h-5 w-5" /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#555]">Office</p>
                  <p className="text-[#1A1A1A] text-lg mt-1 leading-relaxed" data-testid="contact-address">{ADDRESS}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-black/5 p-8">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-full brand-gradient flex items-center justify-center text-white shrink-0"><Phone className="h-5 w-5" /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#555]">Phone</p>
                  <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone" className="text-[#1A1A1A] text-lg mt-1 block hover:text-[#7A2E5D]">{PHONE_DISPLAY}</a>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-black/5 p-8">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-full brand-gradient flex items-center justify-center text-white shrink-0"><Mail className="h-5 w-5" /></div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#555]">Email</p>
                  <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="text-[#1A1A1A] text-lg mt-1 block hover:text-[#7A2E5D]">{EMAIL}</a>
                </div>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <a href={`tel:${PHONE_TEL}`} data-testid="contact-call-cta" className="flex-1 text-center border-2 border-[#7A2E5D] text-[#7A2E5D] rounded-full px-6 py-3 font-medium hover:bg-[#7A2E5D] hover:text-white transition-all">Call Now</a>
              <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-cta" className="flex-1 text-center brand-gradient text-white rounded-full px-6 py-3 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all inline-flex items-center justify-center gap-2">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </FadeUp>

          {/* Form */}
          <FadeUp delay={0.1} className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-black/5 p-8 md:p-10 space-y-5" data-testid="contact-form">
              <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] tracking-tight">Send a message</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="c-name">Name *</label>
                  <input id="c-name" name="name" type="text" value={form.name} onChange={handleChange} data-testid="contact-input-name" className="w-full rounded-xl border border-gray-200 px-4 py-4 text-base focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10" placeholder="Your full name" />
                  {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="c-phone">Phone *</label>
                  <input id="c-phone" name="phone" type="tel" value={form.phone} onChange={handleChange} data-testid="contact-input-phone" className="w-full rounded-xl border border-gray-200 px-4 py-4 text-base focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10" placeholder="+91 00000 00000" />
                  {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="c-req">Project / Requirement</label>
                <input id="c-req" name="requirement" type="text" value={form.requirement} onChange={handleChange} data-testid="contact-input-requirement" className="w-full rounded-xl border border-gray-200 px-4 py-4 text-base focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10" placeholder="2 BHK in Ramapuram" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2" htmlFor="c-msg">Message</label>
                <textarea id="c-msg" name="message" rows={4} value={form.message} onChange={handleChange} data-testid="contact-input-message" className="w-full rounded-xl border border-gray-200 px-4 py-4 text-base focus:outline-none focus:border-[#7A2E5D] focus:ring-2 focus:ring-[#7A2E5D]/10 resize-none" placeholder="Tell us a little more (optional)" />
              </div>
              <button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn" className="brand-gradient text-white rounded-full px-8 py-4 w-full md:w-auto font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:cursor-not-allowed disabled:opacity-60">
                {isSubmitting ? "Saving enquiry..." : "Send via WhatsApp"}
              </button>
            </form>
          </FadeUp>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp>
            <div className="rounded-2xl overflow-hidden border border-black/5 shadow-sm">
              <iframe
                title="Sanggavai Constructions Office Location"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Ramapuram,Chennai&output=embed"
                width="100%"
                height="450"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
